import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef, TemplateRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { FieldsService } from 'src/app/services/fields.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { trigger, transition, style, animate } from '@angular/animations';
import flatpickr from 'flatpickr';
import moment from 'moment';

import {
  DAY_OPTIONS, DAY_MAP, TIME_OPTIONS,
  formatTime, formatDurationShort, formatEventDate, toLocalDateString,
  getInstructorName, calculatePreviewDates, createPopoverOverlay,
  PreviewEvent, PreviewDateConfig
} from './standing-series-utils';

export { PreviewEvent } from './standing-series-utils';

export interface RecurrenceConfig {
  days: string[];
  repetitionUnits: string;
  repetitionFrequency: number;
  endMode: string;
  expirationDate: string;
  alternateInstructors: string[];
  notes: string;
  previewCount: number;
  excludedDates: string[];
  dateOverrides: { [date: string]: { time?: string; instructor?: string } };
  hasPreviewConflicts: boolean;
  eventInstructors?: { [date: string]: string };
}

export interface StandingSeriesEvent {
  id: number;
  date: string;
  day_of_week: string;
  bundle: string;
  instructor_id: string | null;
  status_tid: string | null;
  duration: string | null;
}

export interface StandingSeries {
  id: number;
  student_account_id: string;
  days: string[];
  time: string;
  duration: string;
  instructor_id: string;
  alternate_instructors: string[];
  use_alternating: boolean;
  repetition_frequency: number;
  repetition_units: string;
  end_mode: string;
  expiration_date: string;
  start_date: string;
  day_overrides: { [key: string]: { time?: string; instructor?: string; duration?: string } };
  lesson_type: string | null;
  notes: string | null;
  active: boolean;
  enrollment_id: string | null;
  enrollment_lessons_available: number;
  events: StandingSeriesEvent[];
}

@Component({
  selector: 'app-standing-series',
  templateUrl: './standing-series.component.html',
  styleUrls: ['./standing-series.component.css'],
  animations: [
    trigger('slideFade', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ opacity: 1, height: '*' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, height: '*', overflow: 'hidden' }),
        animate('150ms ease-in', style({ opacity: 0, height: 0 }))
      ])
    ])
  ]
})
export class StandingSeriesComponent implements OnInit, OnChanges, OnDestroy {

  // Inputs from parent form
  @Input() studentAccountId: string = '';
  @Input() instructorId: string = '';
  @Input() duration: string = '00:45';
  @Input() time: string = '';
  @Input() startDate: string = '';
  @Input() lessonType: string = '';
  @Input() bundle: string = 'lesson';
  @Input() selectedEnrollment: any = null;
  // Bundles like services / group_lesson / schedules don't carry an enrollment
  // on the attendee. Setting this to false hides the "Until enrollment ends"
  // option so users only see the modes that actually apply.
  @Input() supportsEnrollment: boolean = true;
  // Whether a student account must be selected before the series can be made
  // recurring. True for lesson/services (the series is built around one
  // student). False for group_lesson/schedules, which generate attendee-less
  // event shells. There's no student to wait for, so the "Make Recurring"
  // toggle is always enabled and the "Select an attendee first" hint never
  // shows (otherwise studentAccountId='' would leave the toggle permanently
  // disabled for those bundles).
  @Input() requiresStudent: boolean = true;
  // Whether an instructor/operator must be selected before the series can be
  // made recurring. False for lesson/services (gated on the student instead).
  // True for group_lesson/schedules, which are operator-driven and attendee-
  // less: the parent binds instructorId to the selected operator so the toggle
  // stays disabled (with a "Select an instructor first" hint) until one is
  // picked, matching the student gate on the other bundles.
  @Input() requiresInstructor: boolean = false;

  // Output to parent
  @Output() recurrenceChanged = new EventEmitter<RecurrenceConfig | null>();

  // Toggle state
  isRecurring = false;

  // Recurrence config (local state)
  days: string[] = [];
  repetitionUnits: string = 'weekly';
  repetitionFrequency: number = 1;
  endMode: string = 'date';
  expirationDate: string = '';
  alternateInstructors: string[] = [];
  notes: string = '';

  // Shared constants exposed to template
  timeOptions = TIME_OPTIONS;
  dayOptions = DAY_OPTIONS;

  // Preview
  previewEvents: PreviewEvent[] = [];

  // Excluded dates (skipped events)
  excludedDates = new Set<string>();

  // Per-date overrides (keyed by date string YYYY-MM-DD)
  dateOverrides: { [date: string]: { time?: string; instructor?: string } } = {};

  // Selected event for popover editing
  selectedEvent: PreviewEvent | null = null;
  editTime: string = '';
  editInstructor: string = '';
  editDateTime: string = '';  // full datetime for conflict checker

  // CDK Overlay for event popover
  @ViewChild('eventPopoverTpl') eventPopoverTpl!: TemplateRef<any>;
  @ViewChild('popoverConflicts') popoverConflicts: any;
  private overlayRef: OverlayRef | null = null;

  // CDK Overlay for rotation order popover
  @ViewChild('rotationPopoverTpl') rotationPopoverTpl!: TemplateRef<any>;
  private rotationOverlayRef: OverlayRef | null = null;

  // Flatpickr for end date
  @ViewChild('endDateInput') endDateInput!: ElementRef;
  private endDateFlatpickr?: flatpickr.Instance;
  endDateDisplayValue: string = '';

  // Enrollment-based end mode
  enrollmentLessonsAvailable: number = 0;
  enrollmentLessonsUsed: number = 0;
  enrollmentLessonsTotal: number = 0;
  enrollmentName: string = '';

  // Preview layout toggle
  private static readonly LAYOUT_KEY = 'standing_series_preview_layout';
  previewLayout: 'grid' | 'list' = 'grid';

  // Conflict detection
  conflictDates = new Set<string>();
  private conflictCheck$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private shortDateFormat = 'MM/DD/YYYY';
  private shortMonthDateFormat = 'MMM D, YYYY';

  constructor(
    private _drupalRESTService: DrupalRESTService,
    private _authService: AuthService,
    public _fieldsService: FieldsService,
    private localizationService: LocalizationService,
    private studioConfigService: StudioConfigService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {
    this.studioConfigService.getStudioConfig().pipe(takeUntil(this.destroy$)).subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);
      const dayFirst = formats.calendarDateFormat.startsWith('DD');
      this.shortDateFormat = dayFirst ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
      this.shortMonthDateFormat = dayFirst ? 'D MMM YYYY' : 'MMM D, YYYY';
    });
  }

  ngOnInit(): void {
    // Restore preview layout preference
    const savedLayout = localStorage.getItem(StandingSeriesComponent.LAYOUT_KEY);
    if (savedLayout === 'list' || savedLayout === 'grid') {
      this.previewLayout = savedLayout;
    }

    // Set default expiration 6 months out
    const sixMonths = new Date();
    sixMonths.setMonth(sixMonths.getMonth() + 6);
    this.expirationDate = toLocalDateString(sixMonths);
    this.endDateDisplayValue = this.formatEndDateDisplay(sixMonths);

    // Debounced conflict checking
    this.conflictCheck$.pipe(
      debounceTime(500),
      switchMap(() => this.runConflictChecks()),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      this.conflictDates.clear();
      (response.results || []).forEach((r: any) => {
        if (r.hasConflict) {
          this.conflictDates.add((r.date || '').substring(0, 10));
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isRecurring) {
      const relevant = ['instructorId', 'duration', 'time', 'startDate'];
      if (relevant.some(key => changes[key])) {
        this.rebuildPreview();
      }
      if (changes['selectedEnrollment']) {
        this.updateEnrollmentLessons();
      }
    }
    // If a parent toggles supportsEnrollment off while endMode is on
    // 'enrollment', fall back to 'date' so we don't render an invisible mode.
    if (changes['supportsEnrollment'] && !this.supportsEnrollment && this.endMode === 'enrollment') {
      this.endMode = 'date';
      if (this.isRecurring) {
        this.rebuildPreview();
      }
    }
  }

  ngOnDestroy(): void {
    this.closeEventPopover();
    this.closeRotationPopover();
    this.endDateFlatpickr?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Whether the "Make Recurring" toggle should be disabled because its
   * required selector hasn't been chosen yet. Student-driven bundles
   * (lesson/services) need a student account; operator-driven attendee-less
   * bundles (group_lesson/schedules) need an instructor. A bundle can require
   * neither (then the toggle is always enabled).
   */
  get recurrenceGateUnmet(): boolean {
    if (this.requiresStudent && !this.studentAccountId) {
      return true;
    }
    if (this.requiresInstructor && !this.instructorId) {
      return true;
    }
    return false;
  }

  /** Hint text shown next to a disabled toggle, matching the unmet gate. */
  get recurrenceGateHint(): string {
    if (this.requiresStudent && !this.studentAccountId) {
      return 'Select an attendee first';
    }
    if (this.requiresInstructor && !this.instructorId) {
      return 'Select an instructor first';
    }
    return '';
  }

  onToggleRecurring(): void {
    if (this.isRecurring) {
      if (this.startDate) {
        const dayName = this.getDayNameFromDate(this.startDate);
        if (dayName && !this.days.includes(dayName)) {
          this.days = [dayName];
        }
      }
      this.rebuildPreview();
    } else {
      this.days = [];
      this.previewEvents = [];
      this.excludedDates.clear();
      this.dateOverrides = {};
      this.closeEventPopover();
      this.selectedEvent = null;
      this.conflictDates.clear();
      this.recurrenceChanged.emit(null);
    }
  }

  toggleDay(day: string): void {
    const idx = this.days.indexOf(day);
    if (idx >= 0) {
      this.days.splice(idx, 1);
      // Clean up date overrides for events on this removed day
      for (const dateStr of Object.keys(this.dateOverrides)) {
        const d = new Date(dateStr + 'T12:00:00');
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        if (dayNames[d.getDay()] === day) {
          delete this.dateOverrides[dateStr];
        }
      }
    } else {
      this.days.push(day);
    }
    this.rebuildPreview();
  }

  isDaySelected(day: string): boolean {
    return this.days.includes(day);
  }

  // --- Event card interactions ---

  isEventExcluded(evt: PreviewEvent): boolean {
    return this.excludedDates.has(evt.date);
  }

  isEventConflict(evt: PreviewEvent): boolean {
    return this.conflictDates.has(evt.date);
  }

  isEventSelected(evt: PreviewEvent): boolean {
    return this.selectedEvent?.date === evt.date;
  }

  toggleExclude(evt: PreviewEvent, event: MouseEvent): void {
    event.stopPropagation();
    if (this.excludedDates.has(evt.date)) {
      this.excludedDates.delete(evt.date);
    } else {
      this.excludedDates.add(evt.date);
      if (this.selectedEvent?.date === evt.date) {
        this.selectedEvent = null;
      }
    }
    this.emitConfigOnly();
  }

  selectEvent(evt: PreviewEvent, cardElement?: HTMLElement): void {
    if (this.isEventExcluded(evt)) return;

    if (this.selectedEvent?.date === evt.date) {
      this.cancelEdit();
      return;
    }

    this.selectedEvent = { ...evt };
    const override = this.dateOverrides[evt.date];
    this.editTime = override?.time || this.time || '';
    this.editInstructor = override?.instructor || evt.instructorId || this.instructorId || '';
    this.updateEditDateTime();

    if (cardElement) {
      this.openEventPopover(cardElement);
      setTimeout(() => this.popoverConflicts?.checkScheduleConflicts(), 0);
    }
  }

  cancelEdit(): void {
    this.closeEventPopover();
    this.selectedEvent = null;
  }

  onEditTimeChange(): void {
    this.updateEditDateTime();
    this.popoverConflicts?.checkScheduleConflicts();
  }

  onEditInstructorChange(): void {
    this.popoverConflicts?.checkScheduleConflicts();
  }

  onPopoverAlternativeTime(newDateTime: string): void {
    if (!newDateTime || !this.selectedEvent) return;
    const timePart = newDateTime.substring(11, 16);
    this.editTime = timePart;
    this.updateEditDateTime();
    this.popoverConflicts?.checkScheduleConflicts();
  }

  private updateEditDateTime(): void {
    if (this.selectedEvent && this.editTime) {
      this.editDateTime = `${this.selectedEvent.date}T${this.editTime}:00`;
    }
  }

  saveEventEdit(): void {
    if (!this.selectedEvent) return;
    const dateKey = this.selectedEvent.date;

    const defaultTime = this.time || '';
    // Compare against the event's rotation-assigned instructor (not the primary)
    // so overriding to the primary on a rotation event still registers as a change.
    const evtDefaultInstructor = this.selectedEvent.instructorId || this.instructorId || '';
    const hasTimeOverride = this.editTime && this.editTime !== defaultTime;
    const hasInstructorOverride = this.editInstructor && this.editInstructor !== evtDefaultInstructor;

    if (hasTimeOverride || hasInstructorOverride) {
      this.dateOverrides[dateKey] = {};
      if (hasTimeOverride) {
        this.dateOverrides[dateKey].time = this.editTime;
      }
      if (hasInstructorOverride) {
        this.dateOverrides[dateKey].instructor = this.editInstructor;
      }
    } else {
      delete this.dateOverrides[dateKey];
    }

    this.closeEventPopover();
    this.selectedEvent = null;
    this.rebuildPreview();
  }

  // --- CDK Overlay popover ---

  private openEventPopover(origin: HTMLElement): void {
    this.closeEventPopover();
    this.overlayRef = createPopoverOverlay(
      this.overlay, origin, this.eventPopoverTpl, this.viewContainerRef,
      { width: 300, onClose: () => this.cancelEdit() }
    );
  }

  private closeEventPopover(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  // --- CDK Overlay for rotation order ---

  openRotationPopover(origin: HTMLElement): void {
    if (this.rotationOverlayRef) {
      this.closeRotationPopover();
      return;
    }
    this.rotationOverlayRef = createPopoverOverlay(
      this.overlay, origin, this.rotationPopoverTpl, this.viewContainerRef,
      { width: 300, onClose: () => this.closeRotationPopover() }
    );
  }

  closeRotationPopover(): void {
    if (this.rotationOverlayRef) {
      this.rotationOverlayRef.dispose();
      this.rotationOverlayRef = null;
    }
  }

  /** Get the day-of-week label for the override hint */
  getDayLabel(dayKey: string): string {
    return DAY_MAP[dayKey] || dayKey;
  }

  /** Check if a date has an override */
  hasDateOverride(date: string): boolean {
    return !!this.dateOverrides[date];
  }

  /** Check if any event on a given weekday has an override (for day toggle dot) */
  hasDayOverride(dayKey: string): boolean {
    return this.previewEvents.some(e => e.dayKey === dayKey && !!this.dateOverrides[e.date]);
  }

  // --- Flatpickr for end date ---

  initEndDateFlatpickr(): void {
    if (this.endDateFlatpickr || !this.endDateInput) return;

    this.endDateFlatpickr = flatpickr(this.endDateInput.nativeElement, {
      dateFormat: 'm/d/Y',
      disableMobile: true,
      defaultDate: this.expirationDate ? new Date(this.expirationDate + 'T12:00:00') : undefined,
      onChange: (selectedDates) => {
        if (selectedDates.length > 0) {
          this.expirationDate = toLocalDateString(selectedDates[0]);
          this.endDateDisplayValue = this.formatEndDateDisplay(selectedDates[0]);
          this.emitConfig();
        }
      },
    });
  }

  openEndDatePicker(): void {
    if (!this.endDateFlatpickr) {
      this.initEndDateFlatpickr();
    }
    this.endDateFlatpickr?.open();
  }

  private formatEndDateDisplay(d: Date): string {
    return moment(d).format(this.shortDateFormat);
  }

  // --- Enrollment end mode ---

  onEndModeChange(): void {
    if (this.endMode === 'enrollment') {
      this.updateEnrollmentLessons();
    }
    this.emitConfig();
  }

  updateEnrollmentLessons(): void {
    if (!this.selectedEnrollment) {
      this.enrollmentLessonsAvailable = 0;
      this.enrollmentName = 'No enrollment selected';
      if (this.endMode === 'enrollment') {
        this.rebuildPreview();
      }
      return;
    }

    this.enrollmentLessonsTotal = Number(this.selectedEnrollment.field_enrollment_lesson_count?.[0]?.value || 0);
    this.enrollmentLessonsUsed = Number(this.selectedEnrollment.field_lesson_used?.[0]?.value || 0);
    this.enrollmentLessonsAvailable = Math.max(0, this.enrollmentLessonsTotal - this.enrollmentLessonsUsed);
    this.enrollmentName = this.selectedEnrollment.title?.[0]?.value || '';

    if (this.endMode === 'enrollment') {
      this.rebuildPreview();
    }
  }

  onAlternatesChanged(): void {
    // Sort alternates to match the alphabetical display order so the rotation
    // order the user sees in the dropdown matches what gets saved/generated.
    const nameMap = new Map<string, string>();
    for (const inst of (this._fieldsService.instructorData() || [])) {
      nameMap.set(String(inst.uid), (inst.value || '').toLowerCase());
    }
    this.alternateInstructors.sort((a, b) =>
      (nameMap.get(a) || '').localeCompare(nameMap.get(b) || '')
    );
    this.emitConfig();
  }

  onRotationReorder(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.alternateInstructors, event.previousIndex, event.currentIndex);
    this.emitConfig();
  }

  removeAlternate(index: number): void {
    this.alternateInstructors.splice(index, 1);
    this.emitConfig();
  }

  emitConfig(): void {
    this.rebuildPreview();
  }

  /** Emit config without rebuilding preview (for exclusion changes) */
  private emitConfigOnly(): void {
    if (this.isRecurring && this.days.length > 0) {
      const activeCount = this.previewEvents.filter(e => !this.excludedDates.has(e.date)).length;
      const eventInstructors: { [date: string]: string } = {};
      this.previewEvents.forEach(e => { if (e.instructorId) eventInstructors[e.date] = e.instructorId; });
      this.recurrenceChanged.emit({
        days: [...this.days],
        repetitionUnits: this.repetitionUnits,
        repetitionFrequency: this.repetitionFrequency,
        endMode: this.endMode,
        expirationDate: this.expirationDate,
        alternateInstructors: [...this.alternateInstructors],
        notes: this.notes,
        previewCount: activeCount,
        excludedDates: [...this.excludedDates],
        dateOverrides: { ...this.dateOverrides },
        hasPreviewConflicts: this.conflictDates.size > 0,
        eventInstructors,
      });
    } else {
      this.recurrenceChanged.emit(null);
    }
  }

  // --- Preview calculation ---

  private rebuildPreview(): void {
    const config: PreviewDateConfig = {
      days: this.days,
      time: this.time,
      duration: this.duration,
      instructorId: this.instructorId,
      alternateInstructors: this.alternateInstructors,
      repetitionUnits: this.repetitionUnits,
      repetitionFrequency: this.repetitionFrequency,
      endMode: this.endMode,
      expirationDate: this.expirationDate,
      startDate: this.startDate,
      enrollmentLessonsAvailable: this.enrollmentLessonsAvailable,
      dateOverrides: this.dateOverrides,
      fieldsService: this._fieldsService,
    };
    this.previewEvents = calculatePreviewDates(config);
    this.conflictCheck$.next();
    if (this.isRecurring && this.days.length > 0) {
      const activeCount = this.previewEvents.filter(e => !this.excludedDates.has(e.date)).length;
      const eventInstructors: { [date: string]: string } = {};
      this.previewEvents.forEach(e => { if (e.instructorId) eventInstructors[e.date] = e.instructorId; });
      this.recurrenceChanged.emit({
        days: [...this.days],
        repetitionUnits: this.repetitionUnits,
        repetitionFrequency: this.repetitionFrequency,
        endMode: this.endMode,
        expirationDate: this.expirationDate,
        alternateInstructors: [...this.alternateInstructors],
        notes: this.notes,
        previewCount: activeCount,
        excludedDates: [...this.excludedDates],
        dateOverrides: { ...this.dateOverrides },
        hasPreviewConflicts: this.conflictDates.size > 0,
        eventInstructors,
      });
    } else {
      this.recurrenceChanged.emit(null);
    }
  }

  // --- Conflict detection ---

  private runConflictChecks() {
    const activeEvents = this.previewEvents.filter(e => !this.excludedDates.has(e.date));
    if (activeEvents.length === 0 || !this.instructorId) {
      return of({ results: [] });
    }

    const studioId = this._authService?.selectedStudio?.id || '1';

    const events = activeEvents.map(evt => {
      const override = this.dateOverrides[evt.date];
      const evtTime = override?.time || this.time || '12:00';
      return {
        date: evt.date,
        field_date_and_time: `${evt.date}T${evtTime}:00`,
        field_duration: this.duration || '00:45',
        field_instructor: evt.instructorId || this.instructorId,
        field_studio_reference: studioId,
      };
    });

    return this._drupalRESTService.httpPOST('/api_rest/v1/checkScheduleConflictsBatch', { events }).pipe(
      catchError(() => of({ results: [] }))
    );
  }

  // --- Helpers ---

  get activePreviewCount(): number {
    return this.previewEvents.filter(e => !this.excludedDates.has(e.date)).length;
  }

  private getDayNameFromDate(dateStr: string): string {
    const d = new Date(dateStr + 'T12:00:00');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[d.getDay()] || '';
  }

  formatEventDate(dateStr: string): string {
    if (!dateStr) return '';
    return moment(dateStr.substring(0, 10), 'YYYY-MM-DD', true).format(this.shortMonthDateFormat);
  }

  formatTime(time: string): string {
    return formatTime(time);
  }

  formatDurationShort(dur: string): string {
    return formatDurationShort(dur);
  }

  get sortedInstructors(): any[] {
    const data = this._fieldsService.instructorData();
    if (!data) return [];
    return [...data].sort((a: any, b: any) => (a.value || '').localeCompare(b.value || ''));
  }

  togglePreviewLayout(): void {
    this.previewLayout = this.previewLayout === 'grid' ? 'list' : 'grid';
    localStorage.setItem(StandingSeriesComponent.LAYOUT_KEY, this.previewLayout);
  }

  getInstructorName(uid: string): string {
    return getInstructorName(uid, this._fieldsService);
  }

}
